import axios from 'axios';
import { getDerivedApiBaseUrl, getMarketingSiteOrigin } from '@/utils/hostOrigins';

const API_BASE_URL = getDerivedApiBaseUrl();

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.split('=')[1];
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getCurrentRole = (): string | null => {
  const rawUser = getCookie('arrangelly_user');
  if (!rawUser) return null;

  try {
    const parsed = JSON.parse(rawUser) as Record<string, unknown>;
    const role = parsed.role ?? parsed.Role;
    return typeof role === 'string' ? role.toLowerCase() : null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // send cookies (arrangelly_token) to API for cross-subdomain auth
  headers: {
    'Content-Type': 'application/json',
  },
});

// No Authorization header needed - API uses HttpOnly cookie (arrangelly_token) for auth
api.interceptors.request.use((config) => {
  const role = getCurrentRole();
  // Add tenant header only when superadmin explicitly switched tenant context.
  if (role === 'superadmin') {
    const selectedTenantId = sessionStorage.getItem('selectedTenantId') || localStorage.getItem('selectedTenantId');
    if (selectedTenantId) {
      config.headers['X-Tenant-Id'] = selectedTenantId;
    } else if (config.headers && 'X-Tenant-Id' in config.headers) {
      delete config.headers['X-Tenant-Id'];
    }
  } else {
    // Teachers/students must use their own tenant from token claims.
    sessionStorage.removeItem('selectedTenantId');
    localStorage.removeItem('selectedTenantId');
    if (config.headers && 'X-Tenant-Id' in config.headers) {
      delete config.headers['X-Tenant-Id'];
    }
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      // Login failures should be handled by the login page, not by global redirect logic.
      if (requestUrl.includes('/auth/login')) {
        return Promise.reject(error);
      }
      if (requestUrl.includes('/auth/forgot-password') || requestUrl.includes('/auth/reset-password')) {
        return Promise.reject(error);
      }
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const hostname = window.location.hostname;
      const loginUrl = hostname.startsWith('dashboard.')
        ? `${getMarketingSiteOrigin()}/login`
        : '/login';
      window.location.href = loginUrl;
    }
    return Promise.reject(error);
  }
);

export default api;

